import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  School, Plus, Users, FileText, Loader2, ArrowRight, Copy, Check,
  Crown, Sparkles, BookOpen
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";
import DashboardHeader from "../components/DashboardHeader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const ClassroomsPage = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [classrooms, setClassrooms] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [newClassroom, setNewClassroom] = useState({ name: "", description: "" });
  const [joinCode, setJoinCode] = useState("");
  const [copiedCode, setCopiedCode] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    fetchClassrooms();
    fetchSubscriptionStatus();
  }, []);

  const fetchClassrooms = async () => {
    try {
      const response = await axios.get(`${API}/classrooms`, {
        headers: getAuthHeaders(token)
      });
      setClassrooms(response.data);
    } catch (error) {
      toast.error("Failed to load classrooms");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: getAuthHeaders(token)
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      // Ignore
    }
  };

  const handleCreate = async () => {
    if (!newClassroom.name.trim()) {
      toast.error("Please enter a classroom name");
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/classrooms`, newClassroom, {
        headers: getAuthHeaders(token)
      });
      toast.success("Classroom created!");
      setShowCreate(false);
      setNewClassroom({ name: "", description: "" });
      fetchClassrooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create classroom");
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) {
      toast.error("Please enter a join code");
      return;
    }
    setJoining(true);
    try {
      const response = await axios.post(`${API}/classrooms/join`, 
        { join_code: joinCode },
        { headers: getAuthHeaders(token) }
      );
      toast.success(response.data.message);
      setShowJoin(false);
      setJoinCode("");
      fetchClassrooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join classroom");
    } finally {
      setJoining(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success("Code copied!");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleUpgradePremium = async () => {
    try {
      const response = await axios.post(`${API}/subscription/premium`, 
        { origin_url: window.location.origin },
        { headers: getAuthHeaders(token) }
      );
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start subscription");
    }
  };

  const isTeacher = user?.role === "teacher";

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <DashboardHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#4F46E5]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="classrooms-page">
      <DashboardHeader />

      <main className="container-main py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {isTeacher ? "My Classrooms" : "Enrolled Classes"}
            </h1>
            <p className="text-slate-600">
              {isTeacher ? "Create and manage your classrooms" : "View your enrolled classrooms"}
            </p>
          </div>
          <div className="flex gap-3">
            {!isTeacher && (
              <Button onClick={() => setShowJoin(true)} className="btn-primary gap-2">
                <School className="w-4 h-4" />
                Join Classroom
              </Button>
            )}
            {isTeacher && (
              <Button onClick={() => setShowCreate(true)} className="btn-primary gap-2">
                <Plus className="w-4 h-4" />
                Create Classroom
              </Button>
            )}
          </div>
        </div>

        {/* Premium Banner for Teachers */}
        {isTeacher && !subscriptionStatus?.is_premium && (
          <Card className="mb-8 bg-gradient-to-r from-yellow-50 via-orange-50 to-yellow-50 border-yellow-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center">
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">Upgrade to Premium</h3>
                    <p className="text-slate-600">Create unlimited classrooms, share tests, and track student progress</p>
                  </div>
                </div>
                <Button onClick={handleUpgradePremium} className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white gap-2">
                  <Sparkles className="w-4 h-4" />
                  $14.99/month
                </Button>
              </div>
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                <span className="flex items-center gap-1.5 text-slate-600">
                  <Check className="w-4 h-4 text-green-500" /> Unlimited classrooms
                </span>
                <span className="flex items-center gap-1.5 text-slate-600">
                  <Check className="w-4 h-4 text-green-500" /> Up to 50 students per class
                </span>
                <span className="flex items-center gap-1.5 text-slate-600">
                  <Check className="w-4 h-4 text-green-500" /> Share tests with students
                </span>
                <span className="flex items-center gap-1.5 text-slate-600">
                  <Check className="w-4 h-4 text-green-500" /> Track scores & leaderboards
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Classrooms Grid */}
        {classrooms.length === 0 ? (
          <Card className="card-base">
            <CardContent className="py-16 text-center">
              <School className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                {isTeacher ? "No classrooms yet" : "Not enrolled in any classrooms"}
              </h3>
              <p className="text-slate-500 mb-6">
                {isTeacher 
                  ? "Create your first classroom to start sharing tests with students"
                  : "Ask your teacher for a join code to enroll in a classroom"
                }
              </p>
              {isTeacher ? (
                <Button onClick={() => setShowCreate(true)} className="btn-primary gap-2">
                  <Plus className="w-4 h-4" />
                  Create Classroom
                </Button>
              ) : (
                <Button onClick={() => setShowJoin(true)} className="btn-primary gap-2">
                  <School className="w-4 h-4" />
                  Join Classroom
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {classrooms.map((classroom) => (
              <Card 
                key={classroom.classroom_id} 
                className="card-base hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => navigate(`/classrooms/${classroom.classroom_id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#4F46E5] to-[#14B8A6] rounded-xl flex items-center justify-center mb-3">
                      <School className="w-6 h-6 text-white" />
                    </div>
                    {isTeacher && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyCode(classroom.join_code);
                        }}
                      >
                        {copiedCode === classroom.join_code ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    )}
                  </div>
                  <CardTitle className="text-lg">{classroom.name}</CardTitle>
                  {classroom.description && (
                    <CardDescription className="line-clamp-2">{classroom.description}</CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4 text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {classroom.students?.length || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {classroom.shared_tests?.length || 0}
                      </span>
                    </div>
                    {isTeacher && (
                      <span className="px-2 py-1 bg-slate-100 rounded text-xs font-mono">
                        {classroom.join_code}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create Classroom Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Classroom</DialogTitle>
            <DialogDescription>
              Create a classroom to share tests with your students
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Classroom Name</Label>
              <Input
                value={newClassroom.name}
                onChange={(e) => setNewClassroom(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Biology 101"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                value={newClassroom.description}
                onChange={(e) => setNewClassroom(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Fall 2025 semester"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={creating} className="btn-primary gap-2">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Join Classroom Dialog */}
      <Dialog open={showJoin} onOpenChange={setShowJoin}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Join Classroom</DialogTitle>
            <DialogDescription>
              Enter the join code provided by your teacher
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Join Code</Label>
              <Input
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="e.g., AB12CD34"
                className="mt-1 font-mono text-center text-lg tracking-wider"
                maxLength={8}
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowJoin(false)}>Cancel</Button>
              <Button onClick={handleJoin} disabled={joining} className="btn-primary gap-2">
                {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : <School className="w-4 h-4" />}
                Join
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClassroomsPage;
